import React, { useState } from "react";
import { Phone, Mail, Clock, Send, ArrowRight, MapPin } from "lucide-react";
import { Helmet } from "react-helmet"; // Added for SEO Meta Tags

const Contact = () => {
  // State for form data
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    vision: ""
  });

  const [status, setStatus] = useState({ loading: false, type: "", message: "" });

  // Handle Input Change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Handle Form Submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus({ loading: true, type: "", message: "" });

    try {
      const response = await fetch("https://luxuriyahomes.com/api/contact/add", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus({ loading: false, type: "success", message: "Enquiry Sent Successfully!" });
        setFormData({ firstName: "", lastName: "", email: "", phoneNumber: "", vision: "" });
      } else {
        setStatus({ loading: false, type: "error", message: "Failed to send enquiry." });
      }
    } catch (error) {
      console.error("Error:", error);
      setStatus({ loading: false, type: "error", message: "Server connection failed." });
    }
  };

  // JSON-LD Structure Data for Local Business SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "InteriorDesignStudio",
    "name": "Luxuriya Homes",
    "image": "https://images.unsplash.com/photo-1600607686527-6fb886090705",
    "telephone": "+91 98765 43210",
    "email": "design@luxuriyahomes.com",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Noida",
      "addressRegion": "Uttar Pradesh",
      "addressCountry": "IN"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "28.6280", // Approx Noida Sector 62
      "longitude": "77.3649"
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday"
      ],
      "opens": "10:00",
      "closes": "20:00"
    }
  };

  return (
    <div className="bg-[#f5f5f4] min-h-screen font-[Serif]">
      
      {/* ================= SEO META TAGS ================= */}
      <Helmet>
        <title>Contact Luxuriya Homes | Luxury Interior Design Studio Noida & Delhi</title>
        <meta 
          name="description" 
          content="Contact Luxuriya Homes for bespoke luxury interior design in Noida, Delhi & Lucknow. Call us at +91 72918 32000 or visit our studio to create timeless, elegant spaces." 
        />
        <meta name="keywords" content="Luxury Interior Design Contact, Interior Studio Noida, Bespoke Interiors Delhi, Luxuriya Homes Number, Home Decor Consultation" />
        <link rel="canonical" href="https://www.luxuriyahomes.com/contact" />
        
        {/* Schema Markup */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* ================= HERO ================= */}
      <section className="relative h-[65vh] w-full overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1600607686527-6fb886090705?auto=format&fit=crop&w=2000&q=80"
          className="absolute inset-0 w-full h-full object-cover"
          alt="Modern Luxury Interior Design Living Room" 
          fetchPriority="high" // SEO: Prioritize loading LCP image
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 h-full flex flex-col justify-center items-center text-center px-4 pt-24">
          <p className="uppercase tracking-[0.4em] text-amber-400 text-sm mb-4">
            Luxury Interior Studio
          </p>
          <h1 className="text-4xl md:text-5xl font-light text-white leading-tight">
            Let’s Create <br />
            <span className="italic text-amber-400">Something Timeless</span>
          </h1>
        </div>
      </section>

      {/* ================= INTRO STRIP ================= */}
      <section className="bg-[#f8f7f5] py-16 text-center">
        <h2 className="text-3xl md:text-4xl font-light text-zinc-900 max-w-3xl mx-auto px-4 mb-14">
          Bespoke interiors crafted with elegance, precision & luxury finishes.
        </h2>
      </section>

      {/* ================= CONTACT CARD ================= */}
      <section className="-mt-16 relative z-20 max-w-6xl mx-auto px-4">
        <div className="grid lg:grid-cols-2 bg-white/80 backdrop-blur-xl rounded-2xl shadow-2xl overflow-hidden">

          {/* IMAGE */}
          <div className="hidden lg:block relative">
            <img
              src="https://images.unsplash.com/photo-1615529182904-14819c35db37?auto=format&fit=crop&w=1200&q=80"
              className="w-full h-full object-cover"
              alt="Luxuriya Homes Design Studio Entrance"
              loading="lazy" // SEO: Lazy load off-screen images
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            <div className="absolute bottom-8 left-8 text-white">
              <h3 className="text-2xl font-light">Visit Our Studio</h3>
              <p className="text-sm text-zinc-300 mt-2 flex items-center gap-2">
                <MapPin size={16} className="text-amber-500"/> Noida, Uttar Pradesh
              </p>
            </div>
          </div>

          {/* FORM */}
          <div className="p-10 md:p-14 bg-[#0c0c0c] text-white">
            <h2 className="text-3xl font-light mb-2">Start Your Journey</h2>
            <div className="w-20 h-[2px] bg-amber-500 mb-8" />

            <form className="space-y-6" onSubmit={handleSubmit}>
              <div className="grid md:grid-cols-2 gap-6">
                <input 
                  className="luxury-input" 
                  placeholder="First Name" 
                  name="firstName" 
                  value={formData.firstName} 
                  onChange={handleChange} 
                  required 
                  aria-label="First Name" // SEO/A11y
                />
                <input 
                  className="luxury-input" 
                  placeholder="Last Name" 
                  name="lastName" 
                  value={formData.lastName} 
                  onChange={handleChange} 
                  aria-label="Last Name" // SEO/A11y
                />
              </div>
              <input 
                className="luxury-input" 
                placeholder="Email Address" 
                type="email"
                name="email" 
                value={formData.email} 
                onChange={handleChange} 
                required 
                aria-label="Email Address"
              />
              <input 
                className="luxury-input" 
                placeholder="Phone Number" 
                name="phoneNumber" 
                value={formData.phoneNumber} 
                onChange={handleChange} 
                required 
                aria-label="Phone Number"
              />
              <textarea 
                rows={3} 
                className="luxury-input resize-none" 
                placeholder="Your vision..." 
                name="vision" 
                value={formData.vision} 
                onChange={handleChange} 
                required 
                aria-label="Project Vision"
              />
              
              <button 
                type="submit"
                disabled={status.loading}
                className="w-full mt-6 bg-gradient-to-r from-amber-500 to-amber-700 py-4 uppercase tracking-[0.25em] text-black font-bold flex items-center justify-center gap-3 hover:from-amber-400 hover:to-amber-600 transition-all shadow-lg hover:shadow-amber-500/20 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {status.loading ? "Sending..." : "Send Enquiry"} <Send size={18} />
              </button>

              {/* Status Message */}
              {status.message && (
                <p className={`text-center text-sm tracking-widest mt-4 ${status.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                  {status.message}
                </p>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* ================= INFO CARDS ================= */}
      <section className="max-w-6xl mx-auto px-4 mt-20">
        <div className="grid md:grid-cols-3 gap-8">
          <div className="luxury-info-card">
            <Phone className="icon" />
            <h4>Call Us</h4>
            <p>+91 72918 32000</p>
            <span>Mon–Sat | 9AM–7PM</span>
          </div>
          <div className="luxury-info-card">
            <Mail className="icon" />
            <h4>Email</h4>
            <p>luxuriyahomes@gmail.com</p>
            <span>Reply within 2 hours</span>
          </div>
          <div className="luxury-info-card">
            <Clock className="icon" />
            <h4>Studio Hours</h4>
            <p>10:00 AM – 8:00 PM</p>
            <span>Sunday Closed</span>
          </div>
        </div>
      </section>

      {/* ================= MAP SECTION ================= */}
      <section className="bg-[#f8f7f5] py-24">
        <div className="max-w-6xl mx-auto px-4 grid lg:grid-cols-2 gap-16 items-center">
          
          {/* Text Section */}
          <div className="order-2 lg:order-1">
            <p className="text-amber-600 font-bold tracking-widest uppercase text-xs mb-3">Our Location</p>
            <h2 className="text-4xl md:text-5xl font-serif text-zinc-900 mb-6 leading-tight">
              Welcome to <br/> <span className="text-amber-600">Luxuriya Homes</span>
            </h2>
            <p className="text-zinc-600 leading-relaxed mb-8 text-lg font-light">
              Step into a world where design meets desire. Our flagship studio in <strong>Noida</strong> is a curated gallery of exquisite materials, bespoke furniture, and architectural marvels. 
              Come, experience the texture of luxury and let's discuss how we can transform your space into a legacy.
            </p>
            
            {/* Visit Now Button */}
           <div className="group inline-flex items-center gap-4 text-zinc-900 font-bold uppercase tracking-widest cursor-default select-none">
              <span className="relative">
                Visit Now
                <span className="absolute left-0 -bottom-1 w-full h-[2px] bg-amber-600 animate-blink"></span>
              </span>

              <span className="w-12 h-12 bg-black text-white rounded-full flex items-center justify-center animate-blink-icon group-hover:bg-amber-600 transition-all duration-300 shadow-xl">
                <ArrowRight size={20} />
              </span>
            </div>
          </div>

          {/* Real Colorful Map */}
          <div className="order-1 lg:order-2 rounded-2xl overflow-hidden shadow-2xl border-4 border-white h-[450px] relative group">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3502.285404543781!2d77.3649!3d28.6280!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390ce55c27633391%3A0x108743d961e1273f!2sIthum%20Tower%2C%20Sector%2062%2C%20Noida%2C%20Uttar%20Pradesh!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin"
              className="w-full h-full border-none filter contrast-110 opacity-90 hover:opacity-100 transition-opacity duration-500"
              allowFullScreen={true}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Luxuriya Homes Ithum Tower Noida Location Map"
            ></iframe>
             
             <div className="absolute bottom-4 right-4 bg-white px-4 py-2 rounded shadow-lg text-xs font-bold uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity">
                Open in Maps
             </div>
          </div>

        </div>
      </section>

      {/* ================= STYLES ================= */}
      <style>{`
        .luxury-input {
          width: 100%;
          background: transparent;
          border-bottom: 1px solid #3f3f46;
          padding: 14px 0;
          color: white;
        }
        .luxury-input::placeholder {
          color: #71717a;
          text-transform: uppercase;
          font-size: 12px;
          letter-spacing: 0.1em;
        }
        .luxury-input:focus { border-color: #f59e0b; outline: none; }

        .luxury-info-card {
          background: white;
          border-radius: 20px;
          padding: 40px;
          text-align: center;
          box-shadow: 0 25px 50px rgba(0,0,0,0.08);
          transition: 0.4s;
        }
        .luxury-info-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 40px 80px rgba(0,0,0,0.15);
        }
        .luxury-info-card .icon {
          color: #f59e0b;
          margin-bottom: 12px;
        }
      `}</style>
    </div>
  );
};

export default Contact;